import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { srtToVtt } from '../utils/subtitleUtils';

export interface PreviewableEpisode {
  sequence: number;
  title: string;
  videoUrl: string;
  renditions: Record<string, string>;
  subtitleUrl: string | null;
}

interface EpisodePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  episodes: PreviewableEpisode[];
  initialSequence: number | null;
}

const PREFERRED_RENDITION_HEIGHT = 720;

// One width drives the whole dialog so the header, the 9:16 video box and the footer always agree.
// The last term keeps the box short enough for the header, footer and native controls to stay on screen.
const PREVIEW_WIDTH = 'min(405px, 92vw, calc((100vh - 200px) * 9 / 16))';

const renditionHeight = (label: string) => parseInt(label, 10) || 0;

const sortedRenditionLabels = (renditions: Record<string, string>) =>
  Object.keys(renditions).sort((a, b) => renditionHeight(a) - renditionHeight(b));

/** The rendition the apps would play, falling back to the largest one at or below 720p. */
const defaultRendition = (episode: PreviewableEpisode): string | null => {
  const labels = sortedRenditionLabels(episode.renditions);
  if (labels.length === 0) return null;
  const appDefault = labels.find(label => episode.renditions[label] === episode.videoUrl);
  if (appDefault) return appDefault;
  const upToPreferred = labels.filter(label => renditionHeight(label) <= PREFERRED_RENDITION_HEIGHT);
  return upToPreferred.length > 0 ? upToPreferred[upToPreferred.length - 1] : labels[0];
};

export function EpisodePreviewDialog({ open, onOpenChange, episodes, initialSequence }: EpisodePreviewDialogProps) {
  const [index, setIndex] = useState(0);
  const [rendition, setRendition] = useState<string | null>(null);
  const [playbackFailed, setPlaybackFailed] = useState(false);
  const [subtitleTrackUrl, setSubtitleTrackUrl] = useState<string | null>(null);
  const [subtitleFailed, setSubtitleFailed] = useState(false);

  const episode: PreviewableEpisode | null = episodes[index] ?? null;

  // Jump to the requested episode each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    const requested = episodes.findIndex(candidate => candidate.sequence === initialSequence);
    setIndex(requested >= 0 ? requested : 0);
  }, [open, initialSequence, episodes]);

  useEffect(() => {
    setRendition(episode ? defaultRendition(episode) : null);
    setPlaybackFailed(false);
  }, [episode]);

  // Reels subtitles are uploaded as SubRip, which <track> rejects, so convert them in the browser.
  useEffect(() => {
    setSubtitleTrackUrl(null);
    setSubtitleFailed(false);
    if (!open || !episode?.subtitleUrl) return;

    let cancelled = false;
    let objectUrl: string | null = null;
    fetch(episode.subtitleUrl)
      .then(response => {
        if (!response.ok) throw new Error(`Subtitle request failed with ${response.status}`);
        return response.text();
      })
      .then(text => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([srtToVtt(text)], { type: 'text/vtt' }));
        setSubtitleTrackUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSubtitleFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, episode]);

  const src = episode ? (rendition ? episode.renditions[rendition] : episode.videoUrl) : null;
  const renditionLabels = episode ? sortedRenditionLabels(episode.renditions) : [];
  const hasPrevious = index > 0;
  const hasNext = index < episodes.length - 1;

  const subtitleNote = subtitleTrackUrl
    ? 'Subtitles loaded'
    : subtitleFailed
      ? 'Subtitles could not be loaded for preview'
      : episode?.subtitleUrl
        ? 'Loading subtitles…'
        : 'No subtitles';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-w-none flex-col gap-0 overflow-hidden p-0"
        style={{ '--preview-width': PREVIEW_WIDTH, width: 'var(--preview-width)' } as React.CSSProperties}
      >
        <div className="flex items-start justify-between gap-3 px-4 pb-3 pt-4 pr-10">
          <div className="min-w-0">
            <DialogTitle className="truncate text-base">{episode?.title ?? 'Episode preview'}</DialogTitle>
            <DialogDescription className="text-xs">
              {episodes.length > 0 ? `${index + 1} of ${episodes.length}` : 'No previewable episodes'}
            </DialogDescription>
          </div>
          {renditionLabels.length > 1 && rendition && (
            <Select value={rendition} onValueChange={setRendition}>
              <SelectTrigger className="h-7 w-[92px] text-xs" aria-label="Video quality">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {renditionLabels.map(label => (
                  <SelectItem key={label} value={label} className="text-xs">
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div
          className="relative shrink-0 overflow-hidden bg-black"
          style={{ width: 'var(--preview-width)', height: 'calc(var(--preview-width) * 16 / 9)' }}
        >
          {src && !playbackFailed ? (
            <video
              key={src}
              src={src}
              controls
              autoPlay
              playsInline
              className="absolute inset-0 h-full w-full object-contain"
              data-testid="episode-preview-video"
              onError={() => setPlaybackFailed(true)}
            >
              {subtitleTrackUrl && <track kind="subtitles" src={subtitleTrackUrl} srcLang="und" label="Subtitles" default />}
            </video>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-sm text-white/80">
              <p>{src ? 'This video could not be played in the browser.' : 'No video available for this episode.'}</p>
              {src && (
                <a href={src} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open video in a new tab
                </a>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t px-4 py-3">
          <Button variant="outline" size="sm" onClick={() => setIndex(current => current - 1)} disabled={!hasPrevious}>
            <ChevronLeft className="mr-1 h-4 w-4" />
            Previous
          </Button>
          <span className="min-w-0 flex-1 truncate text-center text-xs text-muted-foreground">{subtitleNote}</span>
          <Button variant="outline" size="sm" onClick={() => setIndex(current => current + 1)} disabled={!hasNext}>
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
